import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trophy, Flame, Award, TrendingUp, Crown, Medal } from "lucide-react";

const positionColor = (pos) => pos === 1 ? "#CCFF00" : pos === 2 ? "#00F0FF" : pos === 3 ? "#FF0055" : "#F5F5F5";

export default function Ranking() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    api.get("/ranking?limit=50")
      .then(({data}) => setData(data))
      .finally(() => setLoadingData(false));
  }, [user, loading, nav]);

  if (!user) return null;

  return (
    <div data-testid="ranking-page" className="grid-bg min-h-[85vh]">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-center gap-3">
          <Trophy className="text-[#CCFF00]" size={40}/>
          <span className="sticker">RANKING GLOBAL</span>
        </div>
        <h1 className="mt-6 font-bebas text-6xl md:text-7xl tracking-tighter">
          TOP <span className="text-[#CCFF00]">CRIAS</span>
        </h1>
        <p className="text-white/60 mt-3 font-manrope">
          {data ? `${data.total_players} jogadores no total. Compete pela grana e pelo topo.` : "Carregando..."}
        </p>

        {/* Own position highlight */}
        {data?.me && (
          <div data-testid="my-position" className="mt-8 brutal-card brutal-shadow" style={{borderColor:'#00F0FF'}}>
            <div className="font-bebas text-xs tracking-widest text-[#00F0FF]">TUA POSIÇÃO</div>
            <div className="mt-2 flex flex-wrap items-center gap-6">
              <div>
                <div className="font-bebas text-6xl text-[#00F0FF]">#{data.me.position}</div>
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="font-bebas text-2xl">{data.me.name}</div>
                <div className="text-white/60 font-manrope text-sm">Nível {data.me.level}</div>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="font-bebas text-3xl text-[#CCFF00]">{data.me.xp}</div>
                  <div className="text-white/50 font-manrope text-xs uppercase">XP</div>
                </div>
                <div>
                  <div className="font-bebas text-3xl text-[#FF0055] flex items-center gap-1"><Flame size={20}/> {data.me.streak_days}</div>
                  <div className="text-white/50 font-manrope text-xs uppercase">Streak</div>
                </div>
                <div>
                  <div className="font-bebas text-3xl">{data.me.achievements}</div>
                  <div className="text-white/50 font-manrope text-xs uppercase">Medalhas</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Top list */}
        <div className="mt-10 space-y-2">
          <div className="grid grid-cols-12 gap-4 px-4 py-2 font-manrope text-xs uppercase tracking-widest text-white/40">
            <div className="col-span-1">#</div>
            <div className="col-span-5">Jogador</div>
            <div className="col-span-2 text-center">XP</div>
            <div className="col-span-2 text-center">Streak</div>
            <div className="col-span-2 text-center">Medalhas</div>
          </div>
          {loadingData && <div className="text-white/40 font-manrope">Carregando ranking...</div>}
          {data?.top?.length === 0 && (
            <div className="brutal-card text-center text-white/50 font-manrope">
              Ninguém no ranking ainda. Seja o primeiro!
            </div>
          )}
          {data?.top?.map((p) => (
            <div
              key={p.user_id}
              data-testid={`rank-${p.position}`}
              className={`grid grid-cols-12 gap-4 items-center brutal-card ${p.is_me ? 'ring-2 ring-[#00F0FF]' : ''}`}
              style={{
                padding: '1rem',
                borderColor: p.is_me ? '#00F0FF' : (p.position <= 3 ? positionColor(p.position) : '#333'),
              }}
            >
              <div className="col-span-1 flex items-center gap-1">
                {p.position === 1 && <Crown className="text-[#CCFF00]" size={20}/>}
                {p.position === 2 && <Medal className="text-[#00F0FF]" size={20}/>}
                {p.position === 3 && <Award className="text-[#FF0055]" size={20}/>}
                <span className="font-bebas text-2xl" style={{color: positionColor(p.position)}}>
                  {p.position}
                </span>
              </div>
              <div className="col-span-5">
                <div className="font-bebas text-xl leading-none">{p.name}{p.is_me && <span className="ml-2 text-[#00F0FF] text-xs">(você)</span>}</div>
                <div className="text-white/50 font-manrope text-xs">Nível {p.level}</div>
              </div>
              <div className="col-span-2 text-center font-bebas text-xl text-[#CCFF00]">{p.xp}</div>
              <div className="col-span-2 text-center font-bebas text-xl text-[#FF0055] flex items-center justify-center gap-1">
                <Flame size={14}/> {p.streak_days}d
              </div>
              <div className="col-span-2 text-center font-bebas text-xl">{p.achievements}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
